import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { canAccessCrm } from "@/lib/api";
import { Lock } from "lucide-react";

/**
 * Route guard for approval-gated features (CRM / Contacts, Coupons, Events).
 * Only approved dealers (Airtable Users.approved = 1) and admins may enter.
 * Everyone else sees an "approval required" notice instead of the page.
 */
export default function RequireApproval({ children }) {
  const { user, ready } = useAuth();

  if (!ready) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-violet-600 border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  if (!canAccessCrm(user)) {
    return (
      <div className="px-4 py-10" data-testid="approval-required">
        <div className="max-w-md mx-auto rounded-2xl border border-violet-200 bg-violet-50/50 p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
            <Lock className="text-violet-700" size={22} />
          </div>
          <h1 className="text-lg font-bold text-zinc-900 mb-1">
            Approval required
          </h1>
          <p className="text-sm text-zinc-600">
            This area is available to approved dealers only. Reach out to your
            sponsor to get your account approved, then you'll unlock CRM,
            Coupons and Events.
          </p>
          <Link
            to="/app/earnings"
            className="mt-4 inline-flex items-center justify-center px-4 h-10 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold"
            data-testid="approval-required-back"
          >
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  return children;
}
